import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const ActionsPdfTextToWord = ({ caseid, tags, settingId, onSaveStatusChange, initialScope }) => {
  const { t } = useTranslation();
  const [sourceFilePath, setSourceFilePath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const sourceRef = useRef(null);
  const targetRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const normalizePath = useCallback((value) => (value || '').replace(/\\/g, '/'), []);

  const getDisplayPath = useCallback((absPath) => {
    if (!absPath) return '';
    if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
      const primaryRoot = normalizePath(allowedRoots[0]);
      const normalized = normalizePath(absPath);
      if (normalized === primaryRoot) return '/';
      if (normalized.startsWith(`${primaryRoot}/`)) {
        const rel = normalized.slice(primaryRoot.length);
        return rel ? `/${rel.replace(/^[\\/]+/, '')}` : '/';
      }
    }
    return absPath;
  }, [restrictToUserFolder, allowedRoots, normalizePath]);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'pdftotoword',
    onSaveStatusChange,
    customFields: {
      sourceFilePath: '',
      targetPath: '/{filename}_text_to_word.docx',
    },
    customComparison: (initial) => {
      const initialSource = getDisplayPath(initial?.sourceFilePath || initial?.sourcePath || '');
      const initialTarget = getDisplayPath(initial?.targetPath || initial?.target || '');
      return initialSource === sourceFilePath && initialTarget === targetPath;
    },
    customSaveData: () => ({
      sourceFilePath: sourceFilePath.trim(),
      targetPath: targetPath.trim(),
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse PDF to Word data JSON:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceFilePath(getDisplayPath(String(parsedData.sourceFilePath || parsedData.sourcePath || '')));
      setTargetPath(getDisplayPath(String(parsedData.targetPath || parsedData.target || '')));
    },
    enableConditions: true,
    initialScope,
  });

  React.useEffect(() => {
    updateSaveDisabledState?.();
  }, [sourceFilePath, targetPath, updateSaveDisabledState]);

  const availableTags = useMemo(() => (
    Array.isArray(tags)
      ? tags.map((tag) => ({
          key: tag.key || tag.value || tag.variable || tag.label,
          label: tag.label || tag.key || tag.value || tag.variable,
          value: tag.value || tag.variable || tag.key || tag.label,
          variable: tag.variable || tag.value || tag.key || tag.label,
          description: tag.description || '',
          type: tag.type || 'text',
          reply: tag.reply || '',
          tag_type: tag.tag_type || '',
        }))
      : []
  ), [tags]);

  const customDataTags = useMemo(() => ([
    { key: 'filename', label: 'Filename', value: 'filename', description: 'Source filename without extension' },
    { key: 'current_date', label: 'Current Date', value: 'current_date', description: 'Current date in YYYY-MM-DD format' },
    { key: 'projectid', label: 'Project ID', value: 'projectid', description: 'Current project identifier' },
  ]), []);

  const { handleBrowseDirectory: handleBrowseSourceDirectory, FolderBrowserComponent: SourceFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setSourceFilePath(getDisplayPath(folderPath));
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderClean = getDisplayPath(folderPath).replace(/[\\/]+$/, '');
        const separator = folderClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderClean}${separator}{filename}_text_to_word.docx`);
      }
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const handleTagSelect = (value) => {
    targetRef.current?.insertTag(value);
    setAnchorEl(null);
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.pdf_text_to_word', 'PDF Text to Word')}
      isSaveDisabled={isSaveDisabled}
      onSave={() => baseHandleSave()}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags: availableTags,
        handleTagSelect,
        anchorEl,
        setAnchorEl: () => setAnchorEl(null),
        menuType: 'target',
        additionalTags: customDataTags,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <PictureAsPdfIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    SOURCE
                  </Box>
                  {t('pdf_to_word.source_title', 'Source PDF')}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                caseid={caseid}
                ref={sourceRef}
                value={sourceFilePath}
                onChange={setSourceFilePath}
                placeholder={t('pdf_to_word.source_path_placeholder', 'Leave empty to use the current event PDF or enter a PDF path')}
                singleLine
                tags={tags}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSourceBrowseDisabled(true);
                    handleBrowseSourceDirectory();
                  }}
                  disabled={sourceBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('pdf_to_word.source_path_help', 'If empty, the current event source is used. Otherwise the selected PDF file is converted.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <DescriptionIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    OUTPUT
                  </Box>
                  {t('pdf_to_word.target_title', 'Target DOCX')}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                caseid={caseid}
                ref={targetRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('pdf_to_word.target_path_placeholder', '/output/{filename}_text_to_word.docx')}
                singleLine
                onTagClick={(event) => setAnchorEl(event.currentTarget)}
                tags={tags}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('pdf_to_word.target_path_help', 'Output path for the generated DOCX file. Use tags like {filename} for dynamic naming.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TagsDropdown
        tags={availableTags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={() => setAnchorEl(null)}
        menuType="target"
        additionalTags={customDataTags}
      />
      {SourceFolderBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsPdfTextToWord);
